import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { getCleanUrl, isExternalUrl, media } from 'helpers'
import styled from 'styled-components'

import HideVisually from 'js/components/HideVisually/HideVisually'
import Img from 'js/components/Img/Img'

import ArrowRightIcon from 'icons/chevron-right.svg'
import FetchIcon from 'icons/fetch.svg'
import ExternalIcon from 'icons/new-window-small.svg'

const Wrapper = styled.div`
    position: relative;
    overflow: hidden;

    .InfiniteScrollTopShadow,
    .InfiniteScrollBottomShadow {
        position: absolute;
        z-index: 1;
        left: 0;
        right: 0;
        height: 100px;
        pointer-events: none;
        transition: box-shadow ${(p) => p.theme.transition.normal};
    }

    .InfiniteScrollTopShadow {
        top: -100px;
    }

    .InfiniteScrollBottomShadow {
        bottom: -100px;
    }
`

const ScrollBox = styled.div`
    overflow-y: auto;
`

const ShowMoreButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 40px;
    font-size: ${(p) => p.theme.font.size.small};
    line-height: ${(p) => p.theme.font.lineHeight.small};
    font-weight: ${(p) => p.theme.font.weight.semibold};
    border-radius: ${(p) => p.theme.radius.small};

    &:hover {
        background-color: ${(p) => p.theme.color.hover};
    }

    &:active {
        background-color: ${(p) => p.theme.color.active};
    }

    svg {
        margin-right: 7px;
    }
`

const ListLinks = ({ template, links, numberOfItems }) => {
    const { t } = useTranslation()
    const refScrollBox = useRef()
    const refTopShadow = useRef()
    const refBottomShadow = useRef()

    const totalNumberOfItems = links.length

    const [numberOfItemsShown, setNumberOfItemsShown] = useState(numberOfItems)

    useEffect(() => {
        setNumberOfItemsShown(numberOfItems)
    }, [numberOfItems])

    if (numberOfItems && numberOfItemsShown) {
        links = links.slice(0, numberOfItemsShown)
    }

    useLayoutEffect(() => {
        // Set initial height (make it scrollable using isScrollableBox)
        const scrollBox = refScrollBox.current
        if (!!scrollBox && numberOfItems) {
            scrollBox.style.height = scrollBox.clientHeight + 16 + 'px'
            scrollBox.style.flexGrow = 1
        }
    }, [])

    //@todo: we should turn this scrollbox with shadows into a component
    const handleScrollBox = (evt) => {
        const scrollBox = evt.target
        const scrollDistanceLeft =
            scrollBox.scrollHeight -
            (scrollBox.scrollTop + scrollBox.offsetHeight)

        setTopShadow(scrollBox.scrollTop)
        setBottomShadow(scrollDistanceLeft)
    }

    const setTopShadow = (scrollTop) => {
        if (scrollTop > 0) {
            refTopShadow.current.style.boxShadow =
                '0 1px 6px 0 rgba(0, 0, 0, 0.075), 0 20px 20px -15px rgba(0, 0, 0, 0.075)'
        } else if (scrollTop === 0) {
            refTopShadow.current.style.boxShadow = ''
        }
    }

    const setBottomShadow = (scrollDistanceLeft) => {
        if (scrollDistanceLeft >= 5) {
            refBottomShadow.current.style.boxShadow =
                '0 -1px 6px 0 rgba(0, 0, 0, 0.075), 0 -20px 20px -15px rgba(0, 0, 0, 0.075)'
        } else if (scrollDistanceLeft < 5) {
            refBottomShadow.current.style.boxShadow = ''
        }
    }

    return (
        <Wrapper>
            <ScrollBox ref={refScrollBox} onScroll={handleScrollBox}>
                <ul style={{ paddingTop: '12px', paddingBottom: '8px' }}>
                    {links.map(({ title, url, image }, i) => {
                        return (
                            <ListLink
                                key={i}
                                title={title || url}
                                url={url}
                                image={template.includes('image') && image}
                            />
                        )
                    })}
                </ul>
                {numberOfItems && numberOfItemsShown < totalNumberOfItems && (
                    <ShowMoreButton
                        type="button"
                        onClick={() =>
                            setNumberOfItemsShown(
                                numberOfItemsShown + numberOfItems,
                            )
                        }
                    >
                        <FetchIcon />
                        {t('action.show-items')}
                    </ShowMoreButton>
                )}
            </ScrollBox>
            <div ref={refTopShadow} className="InfiniteScrollTopShadow" />
            <div ref={refBottomShadow} className="InfiniteScrollBottomShadow" />
        </Wrapper>
    )
}

const ListLinkWrapper = styled.li`
    &:not(:last-child) {
        border-bottom: 1px solid ${(p) => p.theme.color.grey[20]};
    }

    > a {
        display: flex;
        align-items: center;

        font-size: ${(p) => p.theme.font.size.small};
        line-height: ${(p) => p.theme.font.lineHeight.small};
        color: ${(p) => p.theme.color.text.black};

        &:not([disabled]) {
            padding-top: 10px;
            padding-bottom: 10px;
        }

        &:hover .LinkItemTitle {
            text-decoration: underline;
        }

        ${media.mobileLandscapeDown`
            padding: 0 ${(p) => p.theme.padding.horizontal.small};
        `};

        ${media.tabletUp`
            padding: 0 ${(p) => p.theme.padding.horizontal.normal};
        `};
    }

    .ConfigureLinkItemImage {
        width: 20px;
        margin-right: 6px;
    }

    .LinkItemTitle {
        min-height: ${(p) => p.theme.font.lineHeight.small};
        margin-right: auto;
        color: ${(p) => p.theme.color.primary.main};
    }

    .LinkItemIcon {
        width: 12px;
        flex-shrink: 0;
        margin-left: 8px;

        svg {
            margin: 0 auto;
        }
    }
`

const ListLink = ({ title, url, image }) => {
    const { t } = useTranslation()
    const location = useLocation()

    const cleanUrl = getCleanUrl(url)
    const isExternal = isExternalUrl(cleanUrl)
    const LinkElement = isExternal ? 'a' : Link

    return (
        <ListLinkWrapper>
            <LinkElement
                href={isExternal ? cleanUrl : null}
                to={!isExternal ? cleanUrl : null}
                state={
                    !isExternal
                        ? {
                              prevPathname: location.pathname,
                          }
                        : null
                }
                target={isExternal ? '_blank' : null}
                rel={isExternal ? 'noopener noreferrer' : null}
            >
                {image && (
                    <Img
                        src={image?.url}
                        align="center"
                        aria-hidden
                        className="ConfigureLinkItemImage"
                    />
                )}
                <div className="LinkItemTitle">
                    {title}
                    {isExternal && (
                        <HideVisually>
                            {t('global.opens-in-new-window')}
                        </HideVisually>
                    )}
                </div>
                <div className="LinkItemIcon">
                    {!isExternal ? <ArrowRightIcon /> : <ExternalIcon />}
                </div>
            </LinkElement>
        </ListLinkWrapper>
    )
}

export default ListLinks
